import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Car, GraduationCap,
  MapPin, Phone, PoundSterling, Clock, Building, Target, Users,
  Sparkles, Zap, TrendingUp, Calendar as CalendarIcon, Workflow, Rocket,
  Award,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import CoreJourneyMantra from "../components/CoreJourneyMantra";
import SpecialtyPicker from "../components/instructor/SpecialtyPicker";
import { formatApiError } from "../utils/apiError";

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════
   Step definitions (must mirror backend /app/backend/onboarding.py)
   ═══════════════════════════════════════════════════════════════ */
const SHARED_STEPS = ["welcome", "location", "business", "contact", "specialisms", "pricing", "hours", "current_system"];
const ADI_STEPS = ["adi_pupils", "adi_operation", "adi_trainer_interest", "adi_revenue"];
const PDI_STEPS = ["pdi_stage", "pdi_part2", "pdi_supervisor", "pdi_baseline", "pdi_target_date"];

function stepsFor(role) {
  const branch = role === "pdi" ? PDI_STEPS : ADI_STEPS;
  return [...SHARED_STEPS, ...branch, "review"];
}

export default function InstructorOnboarding() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [role, setRole] = useState("adi");
  const [steps, setSteps] = useState(stepsFor("adi"));
  const [idx, setIdx] = useState(0);
  const [data, setData] = useState({});
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: s } = await axios.get(`${API}/api/onboarding/status`, { withCredentials: true });
      if (s.complete) { nav("/instructor"); return; }
      setRole(s.role || "adi");
      const st = s.steps || stepsFor(s.role || "adi");
      setSteps(st);
      setData(s.data || {});
      setPlan(s.plan || null);
      const resumeIdx = Math.max(0, st.indexOf(s.current_step || "welcome"));
      setIdx(resumeIdx);
    } catch {
      // stay on wizard — user might not be instructor
    }
    setLoading(false);
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const step = steps[idx];
  // "Real" setup steps exclude the activation welcome (step 0) and final
  // completion (last). We only number these for the progress header.
  const isActivation = step === "welcome";
  const isCompletion = step === "review";
  const setupTotal = Math.max(1, steps.length - 2);
  const setupIdx = Math.max(0, Math.min(setupTotal, idx));
  const pct = useMemo(() => {
    if (isActivation) return 0;
    if (isCompletion) return 100;
    return Math.round(((setupIdx) / setupTotal) * 100);
  }, [isActivation, isCompletion, setupIdx, setupTotal]);

  // Rotating confidence microcopy between real setup steps — keeps momentum
  // without adding friction. Order matches the UX spec.
  const CONFIDENCE_LINES = [
    "Nice choice. Let's get you operational.",
    "This will save you time later.",
    "We're building your daily system.",
    "Your diary is about to feel lighter.",
    "Almost ready to run.",
  ];
  const confidence = !isActivation && !isCompletion
    ? CONFIDENCE_LINES[Math.min(setupIdx - 1, CONFIDENCE_LINES.length - 1)] || CONFIDENCE_LINES[0]
    : null;

  const save = async (stepKey, payload) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/onboarding/save`,
        { step: stepKey, data: payload }, { withCredentials: true });
      setData(prev => ({ ...prev, [stepKey]: payload }));
    } catch {
      toast.error("Didn't save — give it another go.");
      setSaving(false);
      return false;
    }
    setSaving(false);
    return true;
  };

  const next = async (payload = {}) => {
    const ok = await save(step, payload);
    if (ok) setIdx(i => Math.min(i + 1, steps.length - 1));
  };
  const back = () => setIdx(i => Math.max(0, i - 1));

  const finish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/onboarding/complete`, {}, { withCredentials: true });
      await refreshUser?.();
      // Enter Work Mode by default for ADI; PDI still lands on their hub.
      nav(role === "pdi" ? "/pdi-hub" : "/work-mode");
    } catch {
      toast.error("Couldn't finish setup — give it another go.");
    }
    setSaving(false);
  };

  const viewDashboard = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/onboarding/complete`, {}, { withCredentials: true });
      await refreshUser?.();
      nav(role === "pdi" ? "/pdi-hub" : "/instructor");
    } catch {
      toast.error("Couldn't finish setup — give it another go.");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center gap-3">
          {idx > 0 && !isActivation && !isCompletion && (
            <button data-testid="onb-back" onClick={back} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button>
          )}
          <div className="flex-1">
            <p className="text-xs font-extrabold tracking-wider text-blue-600 uppercase">
              {isActivation ? "Activate your business" : isCompletion ? "All set" : (role === "pdi" ? "Trainee Setup" : "Instructor Setup")}
            </p>
            {!isActivation && !isCompletion && (
              <p className="text-[10px] text-slate-500 dark:text-slate-400" data-testid="onb-step-counter">
                Step {setupIdx} of {setupTotal}
              </p>
            )}
          </div>
          {!isActivation && !isCompletion && <div className="text-[11px] font-bold text-slate-500">{pct}%</div>}
        </div>
        <div className="h-1 bg-slate-100 dark:bg-slate-700">
          <div data-testid="onb-progress" className="h-1 bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
        {confidence && (
          <div data-testid="onb-confidence" className="max-w-xl mx-auto px-4 py-2 text-[11px] font-bold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {confidence}
          </div>
        )}
      </header>

      <div className="max-w-xl mx-auto px-4 py-6">
        <StepCard
          step={step}
          role={role}
          data={data}
          plan={plan}
          onPlanSelected={setPlan}
          onNext={next}
          onFinish={finish}
          onViewDashboard={viewDashboard}
          saving={saving}
        />
      </div>
    </div>
  );
}

/* ─── Step renderer ─── */
function StepCard({ step, role, data, plan, onPlanSelected, onNext, onFinish, onViewDashboard, saving }) {
  const existing = data?.[step] || {};
  switch (step) {
    case "welcome":       return <StepActivation role={role} plan={plan} onPlanSelected={onPlanSelected} onNext={onNext} saving={saving} />;
    case "location":      return <StepLocation v={existing} onNext={onNext} saving={saving} />;
    case "business":      return <StepBusiness v={existing} onNext={onNext} saving={saving} />;
    case "contact":       return <StepContact v={existing} onNext={onNext} saving={saving} />;
    case "specialisms":   return <StepSpecialisms v={existing} onNext={onNext} saving={saving} />;
    case "pricing":       return <StepPricing v={existing} onNext={onNext} saving={saving} />;
    case "hours":         return <StepHours v={existing} onNext={onNext} saving={saving} />;
    case "current_system":return <StepCurrentSystem v={existing} onNext={onNext} saving={saving} />;
    // ADI
    case "adi_pupils":    return <StepAdiPupils v={existing} onNext={onNext} saving={saving} />;
    case "adi_operation": return <StepAdiOperation v={existing} onNext={onNext} saving={saving} />;
    case "adi_trainer_interest": return <StepAdiTrainer v={existing} onNext={onNext} saving={saving} />;
    case "adi_revenue":   return <StepAdiRevenue v={existing} onNext={onNext} saving={saving} />;
    // PDI
    case "pdi_stage":     return <StepPdiStage v={existing} onNext={onNext} saving={saving} />;
    case "pdi_part2":     return <StepPdiPart2 v={existing} onNext={onNext} saving={saving} />;
    case "pdi_supervisor":return <StepPdiSupervisor v={existing} onNext={onNext} saving={saving} />;
    case "pdi_baseline":  return <StepPdiBaseline v={existing} onNext={onNext} saving={saving} />;
    case "pdi_target_date":return <StepPdiTargetDate v={existing} onNext={onNext} saving={saving} />;
    case "review":        return <StepComplete role={role} plan={plan} onFinish={onFinish} onViewDashboard={onViewDashboard} saving={saving} />;
    default: return null;
  }
}

/* ─── Shared UI primitives ─── */
const Card = ({ icon: Icon, title, subtitle, children, testId }) => (
  <div data-testid={testId} className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
    {Icon && <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-blue-600" />
    </div>}
    <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-1">{title}</h1>
    {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">{subtitle}</p>}
    {children}
  </div>
);

const NextBtn = ({ onClick, disabled, saving, label = "Continue", testId = "onb-next" }) => (
  <Button data-testid={testId} onClick={onClick} disabled={disabled || saving}
    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl mt-6">
    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{label}<ArrowRight className="w-4 h-4 ml-2" /></>}
  </Button>
);

/* ═══════════════ ACTIVATION — Welcome + Plan Selection ═══════════════
   Two-phase opener: hero welcome → plan picker. On plan pick we persist
   the plan to the backend, then auto-advance into the real setup steps.
*/
const ACTIVATION_PLANS = [
  {
    id: "basic",
    label: "Basic",
    tagline: "Perfect to get organised fast.",
    recommended: false,
    works_for_you: [
      "Lessons organised",
      "Calendar centralised",
      "Notes in one place",
    ],
    accent: "from-slate-700 to-slate-900",
    chip: "bg-slate-700 text-white",
  },
  {
    id: "premium",
    label: "Premium",
    tagline: "Recommended — fewer steps, better tools, smoother operation.",
    recommended: true,
    works_for_you: [
      "SMS reminder templates",
      "Progress tracking",
      "Recurring schedule help",
      "Less admin stress",
    ],
    accent: "from-indigo-600 to-blue-600",
    chip: "bg-indigo-600 text-white",
  },
  {
    id: "growth",
    label: "Growth",
    tagline: "Built to grow your business and fill gaps.",
    recommended: false,
    works_for_you: [
      "Enquiry capture",
      "Gap-fill systems",
      "Analytics",
      "Growth tools",
    ],
    accent: "from-emerald-600 to-teal-600",
    chip: "bg-emerald-600 text-white",
  },
];

function StepActivation({ role, plan, onPlanSelected, onNext, saving }) {
  // Resume directly at plan picker if a plan was chosen previously.
  const [phase, setPhase] = useState(plan ? "plan" : "hello");
  const [picking, setPicking] = useState(null);

  if (phase === "hello") {
    return (
      <div data-testid="activation-hello" className="bg-gradient-to-br from-indigo-600 via-blue-600 to-slate-900 rounded-3xl p-7 text-white shadow-xl">
        <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-5">
          {role === "pdi" ? <GraduationCap className="w-6 h-6" /> : <Rocket className="w-6 h-6" />}
        </div>
        <h1 className="text-3xl font-extrabold leading-tight mb-2">Run your driving business smarter.</h1>
        <p className="text-sm text-white/80 mb-3">Choose your plan and we'll help set everything up in minutes.</p>
        <CoreJourneyMantra className="mb-5 bg-white/10 border-white/20 text-white/90" testid="instructor-onboarding-mantra" />
        <ul className="space-y-2 text-[13px] text-white/90 mb-6">
          <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-yellow-300" /> Minimal thinking, maximum movement.</li>
          <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-300" /> Systems working for you in the background.</li>
          <li className="flex items-center gap-2"><Workflow className="w-4 h-4 text-yellow-300" /> Live in minutes, not days.</li>
        </ul>
        <Button
          data-testid="activation-choose-plan"
          onClick={() => setPhase("plan")}
          className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 font-extrabold rounded-xl"
        >
          Choose My Plan <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  const choose = async (p) => {
    setPicking(p.id);
    try {
      await axios.post(`${API}/api/onboarding/plan`, { plan: p.id }, { withCredentials: true });
      onPlanSelected?.(p.id);
      // Move straight into the real setup — no dashboard in between.
      await onNext({ plan: p.id });
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't save plan — try again."));
    }
    setPicking(null);
  };

  return (
    <div data-testid="activation-plan" className="space-y-3">
      <div className="px-1">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600">Pick a plan</p>
        <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">Choose what you want working for you.</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Upgrade or change anytime.</p>
      </div>
      {ACTIVATION_PLANS.map((p) => (
        <div
          key={p.id}
          data-testid={`plan-card-${p.id}`}
          className={`relative rounded-3xl p-5 border-2 bg-white dark:bg-slate-800 shadow-sm transition ${
            p.recommended ? "border-indigo-500" : "border-slate-200 dark:border-slate-700"
          }`}
        >
          {p.recommended && (
            <span data-testid={`plan-recommended-${p.id}`} className="absolute -top-2 left-5 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-indigo-600 text-white">Recommended</span>
          )}
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500">Plan</p>
              <h3 className={`text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br ${p.accent}`}>{p.label}</h3>
              <p className="mt-1 text-[13px] text-slate-600 dark:text-slate-300">{p.tagline}</p>
            </div>
          </div>
          <div className="rounded-2xl bg-slate-50 dark:bg-slate-900/60 p-3 mb-4">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-2">What works for you automatically</p>
            <ul className="space-y-1.5">
              {p.works_for_you.map((x) => (
                <li key={x} className="flex items-start gap-2 text-[13px] text-slate-700 dark:text-slate-200">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <Button
            data-testid={`plan-select-${p.id}`}
            onClick={() => choose(p)}
            disabled={!!picking || saving}
            className={`w-full h-11 font-extrabold rounded-xl text-white ${
              p.recommended ? "bg-indigo-600 hover:bg-indigo-500" : "bg-slate-900 hover:bg-slate-800"
            }`}
          >
            {picking === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start With This Plan<ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════ SHARED STEPS ═══════════════ */

function StepLocation({ v, onNext, saving }) {
  const [postcode, setPostcode] = useState(v.postcode || "");
  const [area, setArea] = useState(v.area || "");
  return (
    <Card icon={MapPin} title="Where do you teach?" subtitle="Helps us match you with pupils nearby.">
      <Label>Postcode</Label>
      <Input data-testid="onb-postcode" value={postcode} onChange={e => setPostcode(e.target.value.toUpperCase())} placeholder="e.g. SW1A 1AA" className="mt-1 mb-4" />
      <Label>Town or area (optional)</Label>
      <Input data-testid="onb-area" value={area} onChange={e => setArea(e.target.value)} placeholder="e.g. Central London" className="mt-1" />
      <NextBtn onClick={() => onNext({ postcode: postcode.trim(), area: area.trim() })} disabled={!postcode.trim()} saving={saving} />
    </Card>
  );
}

function StepBusiness({ v, onNext, saving }) {
  const [businessName, setBusinessName] = useState(v.business_name || "");
  return (
    <Card icon={Building} title="Your business name" subtitle="Skip it if you trade under your own name.">
      <Label>Business name</Label>
      <Input data-testid="onb-business" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. Acme Driving School" className="mt-1" />
      <NextBtn onClick={() => onNext({ business_name: businessName.trim() })} saving={saving} label={businessName.trim() ? "Continue" : "Skip for now"} />
    </Card>
  );
}

function StepContact({ v, onNext, saving }) {
  const [phone, setPhone] = useState(v.phone || "");
  return (
    <Card icon={Phone} title="Best number to reach you" subtitle="Only for pupil reminders and late alerts. Never shown publicly.">
      <Label>Mobile</Label>
      <Input data-testid="onb-phone" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07..." className="mt-1" />
      <NextBtn onClick={() => onNext({ phone: phone.trim() })} disabled={phone.trim().length < 7} saving={saving} />
    </Card>
  );
}

function StepPricing({ v, onNext, saving }) {
  const [rate, setRate] = useState(v.hourly_rate ?? "");
  const [blockDiscount, setBlockDiscount] = useState(v.block_discount ?? "");
  return (
    <Card icon={PoundSterling} title="What do you charge?" subtitle="You can tweak individual lessons later.">
      <Label>Hourly rate (£)</Label>
      <Input data-testid="onb-rate" type="number" inputMode="decimal" value={rate}
        onChange={e => setRate(e.target.value)} placeholder="e.g. 38" className="mt-1 mb-4" />
      <Label>Block-booking discount % (if you offer one)</Label>
      <Input data-testid="onb-block-discount" type="number" inputMode="decimal" value={blockDiscount}
        onChange={e => setBlockDiscount(e.target.value)} placeholder="e.g. 5" className="mt-1" />
      <NextBtn onClick={() => onNext({
        hourly_rate: rate === "" ? null : Number(rate),
        block_discount: blockDiscount === "" ? null : Number(blockDiscount),
      })} disabled={rate === "" || Number(rate) <= 0} saving={saving} />
    </Card>
  );
}

function StepHours({ v, onNext, saving }) {
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const savedDays = Array.isArray(v.days) && v.days.length
    ? v.days
    : ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const [scheduleMode, setScheduleMode] = useState(
    v.schedule_mode || (v.work_block_days ? "rolling_blocks" : "fixed_week")
  );
  const [active, setActive] = useState(savedDays);
  const [daysOff, setDaysOff] = useState(
    v.days_off_per_week ?? Math.max(0, 7 - savedDays.length)
  );
  const [workBlockDays, setWorkBlockDays] = useState(v.work_block_days ?? 7);
  const [offBlockDays, setOffBlockDays] = useState(v.off_block_days ?? 4);
  const [cycleStart, setCycleStart] = useState(
    v.cycle_start_date || new Date().toISOString().slice(0, 10)
  );
  const [maxLessons, setMaxLessons] = useState(v.max_lessons_per_day ?? 5);
  const [blockHours, setBlockHours] = useState(
    Math.max(1, Math.round((v.default_lesson_duration_minutes ?? 120) / 60))
  );
  const [acceptedBlockMinutes, setAcceptedBlockMinutes] = useState(
    Array.isArray(v.accepted_lesson_duration_minutes) && v.accepted_lesson_duration_minutes.length
      ? v.accepted_lesson_duration_minutes
      : [60, 120, 180]
  );
  const [start, setStart] = useState(v.start || "09:00");
  const [end, setEnd] = useState(v.end || "18:00");
  const teachingDayTarget = 7 - Number(daysOff);

  const chooseDaysOff = (count) => {
    const nextCount = Number(count);
    const target = 7 - nextCount;
    setDaysOff(nextCount);
    setActive((current) => {
      const kept = current.filter((day) => DAYS.includes(day)).slice(0, target);
      const additions = DAYS.filter((day) => !kept.includes(day)).slice(0, target - kept.length);
      return [...kept, ...additions];
    });
  };

  const toggle = (day) => setActive((current) => {
    if (current.includes(day)) return current.filter((item) => item !== day);
    if (current.length >= teachingDayTarget) return current;
    return [...current, day];
  });

  const toggleAcceptedBlock = (minutes) => {
    setAcceptedBlockMinutes((current) => {
      if (current.includes(minutes)) {
        if (current.length === 1) return current;
        const next = current.filter((item) => item !== minutes);
        if (Number(blockHours) * 60 === minutes) setBlockHours(next[0] / 60);
        return next;
      }
      return [...current, minutes].sort((a, b) => a - b);
    });
  };

  const validHours = start && end && start < end;
  const validRolling = (
    Number(workBlockDays) >= 1 && Number(workBlockDays) <= 21
    && Number(offBlockDays) >= 1 && Number(offBlockDays) <= 21
    && Boolean(cycleStart)
  );
  const ready = (
    validHours
    && Number(maxLessons) > 0
    && [1, 2, 3].includes(Number(blockHours))
    && acceptedBlockMinutes.length > 0
    && acceptedBlockMinutes.includes(Number(blockHours) * 60)
    && (
      scheduleMode === "rolling_blocks"
        ? validRolling
        : active.length === teachingDayTarget
    )
  );

  const submit = () => onNext({
    schedule_mode: scheduleMode,
    days: scheduleMode === "fixed_week" ? active : DAYS,
    days_off_per_week: scheduleMode === "fixed_week" ? Number(daysOff) : null,
    work_block_days: scheduleMode === "rolling_blocks" ? Number(workBlockDays) : null,
    off_block_days: scheduleMode === "rolling_blocks" ? Number(offBlockDays) : null,
    cycle_start_date: scheduleMode === "rolling_blocks" ? cycleStart : null,
    max_lessons_per_day: Number(maxLessons),
    default_lesson_duration_minutes: Number(blockHours) * 60,
    accepted_lesson_duration_minutes: acceptedBlockMinutes,
    start,
    end,
  });

  return (
    <Card
      icon={Clock}
      title="Shape your working rhythm"
      subtitle="These limits build your calendar on every plan, including Basic."
    >
      <Label className="block mb-2">How do you organise work and rest?</Label>
      <div className="grid grid-cols-2 gap-2 mb-5">
        <button
          type="button"
          data-testid="onb-schedule-fixed"
          onClick={() => setScheduleMode("fixed_week")}
          className={`min-h-14 rounded-xl px-3 text-sm font-bold transition ${
            scheduleMode === "fixed_week"
              ? "bg-blue-600 text-white"
              : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
          }`}
        >
          Same days each week
        </button>
        <button
          type="button"
          data-testid="onb-schedule-rolling"
          onClick={() => setScheduleMode("rolling_blocks")}
          className={`min-h-14 rounded-xl px-3 text-sm font-bold transition ${
            scheduleMode === "rolling_blocks"
              ? "bg-blue-600 text-white"
              : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
          }`}
        >
          Work block, then rest block
        </button>
      </div>

      {scheduleMode === "rolling_blocks" ? (
        <div className="space-y-4 mb-5" data-testid="onb-rolling-cycle">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Consecutive work days</Label>
              <Input
                data-testid="onb-work-block-days"
                type="number"
                inputMode="numeric"
                min="1"
                max="21"
                value={workBlockDays}
                onChange={(event) => setWorkBlockDays(event.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Consecutive days off</Label>
              <Input
                data-testid="onb-off-block-days"
                type="number"
                inputMode="numeric"
                min="1"
                max="21"
                value={offBlockDays}
                onChange={(event) => setOffBlockDays(event.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label>First day of the next work block</Label>
            <Input
              data-testid="onb-cycle-start"
              type="date"
              value={cycleStart}
              onChange={(event) => setCycleStart(event.target.value)}
              className="mt-1"
            />
          </div>
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3">
            <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
              {workBlockDays || 0} days working → {offBlockDays || 0} days off → repeat
            </p>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-300/70 mt-1">
              Your rest days stay together and move naturally through the weekdays.
            </p>
          </div>
        </div>
      ) : (
        <>
          <Label className="block mb-2">How many days off do you want each week?</Label>
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {[0, 1, 2, 3, 4, 5, 6].map((count) => (
              <button
                key={count}
                type="button"
                data-testid={`onb-days-off-${count}`}
                onClick={() => chooseDaysOff(count)}
                className={`h-10 rounded-lg text-sm font-bold transition ${
                  Number(daysOff) === count
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                }`}
              >
                {count}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
            {daysOff === 0 ? "No protected days off." : `${daysOff} protected day${daysOff === 1 ? "" : "s"} off each week.`}
          </p>

          <Label className="block mb-2">
            Which {teachingDayTarget} day{teachingDayTarget === 1 ? "" : "s"} do you want to teach?
          </Label>
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {DAYS.map((day) => (
              <button
                key={day}
                type="button"
                data-testid={`onb-day-${day}`}
                onClick={() => toggle(day)}
                className={`h-10 rounded-lg text-xs font-bold transition ${
                  active.includes(day)
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                }`}
              >
                {day}
              </button>
            ))}
          </div>
          <p className={`text-xs mb-5 ${
            active.length === teachingDayTarget ? "text-slate-500 dark:text-slate-400" : "text-amber-600"
          }`}>
            Selected {active.length} of {teachingDayTarget} teaching days.
          </p>
        </>
      )}

      <Label className="block mb-2">Which booking lengths will you accept?</Label>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {[1, 2, 3].map((hours) => {
          const minutes = hours * 60;
          const accepted = acceptedBlockMinutes.includes(minutes);
          return (
            <button
              key={hours}
              type="button"
              aria-pressed={accepted}
              data-testid={`onb-accept-block-hours-${hours}`}
              onClick={() => toggleAcceptedBlock(minutes)}
              className={`min-h-12 rounded-xl px-2 text-sm font-bold transition ${
                accepted
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 line-through"
              }`}
            >
              {accepted ? "Accept " : "Do not accept "}{hours} hour{hours === 1 ? "" : "s"}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
        Turn 1-hour bookings off if you only want longer lessons. At least one length must stay on.
      </p>

      <Label className="block mb-2">What is your default booking length?</Label>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[1, 2, 3].map((hours) => {
          const accepted = acceptedBlockMinutes.includes(hours * 60);
          return (
            <button
              key={hours}
              type="button"
              disabled={!accepted}
              data-testid={`onb-block-hours-${hours}`}
              onClick={() => setBlockHours(hours)}
              className={`min-h-12 rounded-xl px-2 text-sm font-bold transition ${
                Number(blockHours) === hours
                  ? "bg-violet-600 text-white"
                  : accepted
                    ? "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 opacity-60"
              }`}
            >
              {hours} hour{hours === 1 ? "" : "s"}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-3 mb-5">
        New bookings start with this length, but only accepted lengths are offered to pupils.
      </p>

      <Label className="block mb-2">Maximum lessons in one working day</Label>
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[2, 3, 4, 5, 6, 7, 8, 9].map((count) => (
          <button
            key={count}
            type="button"
            data-testid={`onb-max-lessons-${count}`}
            onClick={() => setMaxLessons(count)}
            className={`h-10 rounded-lg text-sm font-bold transition ${
              Number(maxLessons) === count
                ? "bg-blue-600 text-white"
                : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
            }`}
          >
            {count}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label>Earliest start</Label><Input data-testid="onb-start" type="time" value={start} onChange={e => setStart(e.target.value)} className="mt-1" /></div>
        <div><Label>Latest finish</Label><Input data-testid="onb-end" type="time" value={end} onChange={e => setEnd(e.target.value)} className="mt-1" /></div>
      </div>
      {!validHours && <p className="text-xs text-red-600 mt-2">Finish time must be after start time.</p>}
      <NextBtn onClick={submit} disabled={!ready} saving={saving} />
    </Card>
  );
}

function StepCurrentSystem({ v, onNext, saving }) {
  const [system, setSystem] = useState(v.system || "");
  const [wantsMigration, setWantsMigration] = useState(v.wants_migration ?? null);
  const options = ["Total Drive", "iDriving", "MyDriveTime", "Ezlicence", "Paper diary", "None", "Other"];
  const ask = system && system !== "None";
  return (
    <Card icon={Building} title="What do you use right now?" subtitle="We'll move your pupils and bookings over for you if you're switching.">
      <div className="grid grid-cols-2 gap-2 mb-4">
        {options.map(o => (
          <button key={o} type="button" data-testid={`onb-sys-${o}`} onClick={() => setSystem(o)}
            className={`h-11 rounded-lg text-sm font-semibold transition border ${system === o
              ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600"}`}>
            {o}
          </button>
        ))}
      </div>
      {ask && (
        <>
          <Label className="block mb-2">Want us to bring your pupils and bookings across?</Label>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="onb-migrate-yes" type="button" onClick={() => setWantsMigration(true)}
              className={`h-11 rounded-lg text-sm font-semibold ${wantsMigration === true ? "bg-emerald-600 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"}`}>Yes please</button>
            <button data-testid="onb-migrate-no" type="button" onClick={() => setWantsMigration(false)}
              className={`h-11 rounded-lg text-sm font-semibold ${wantsMigration === false ? "bg-slate-800 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"}`}>I'll do it myself</button>
          </div>
        </>
      )}
      <NextBtn onClick={() => onNext({ system, wants_migration: wantsMigration })} disabled={!system || (ask && wantsMigration === null)} saving={saving} />
    </Card>
  );
}

/* ═══════════════ ADI BRANCH ═══════════════ */
function StepAdiPupils({ v, onNext, saving }) {
  const [count, setCount] = useState(v.count ?? "");
  return (
    <Card icon={Users} title="How many pupils are you teaching right now?" subtitle="A rough number's fine.">
      <Input data-testid="onb-adi-pupils" type="number" inputMode="numeric" value={count}
        onChange={e => setCount(e.target.value)} placeholder="e.g. 18" />
      <NextBtn onClick={() => onNext({ count: count === "" ? null : Number(count) })} disabled={count === ""} saving={saving} />
    </Card>
  );
}

function StepAdiOperation({ v, onNext, saving }) {
  const [mode, setMode] = useState(v.mode || "");
  const opts = [
    { key: "solo", label: "Just me — solo instructor" },
    { key: "franchise", label: "With a franchise" },
    { key: "school", label: "Running my own school" },
  ];
  return (
    <Card icon={Building} title="How do you run things?" subtitle="">
      <div className="grid grid-cols-1 gap-2">
        {opts.map(o => (
          <button key={o.key} type="button" data-testid={`onb-adi-op-${o.key}`} onClick={() => setMode(o.key)}
            className={`h-12 rounded-xl text-sm font-semibold border text-left px-4 ${mode === o.key
              ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <NextBtn onClick={() => onNext({ mode })} disabled={!mode} saving={saving} />
    </Card>
  );
}

function StepAdiTrainer({ v, onNext, saving }) {
  const [interested, setInterested] = useState(v.interested ?? null);
  return (
    <Card icon={GraduationCap} title="Interested in training PDIs?"
      subtitle="ORDIT trainers earn more — we can walk you through qualifying.">
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="onb-trainer-yes" type="button" onClick={() => setInterested(true)}
          className={`h-12 rounded-xl text-sm font-bold ${interested === true ? "bg-emerald-600 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"}`}>Yes — tell me more</button>
        <button data-testid="onb-trainer-no" type="button" onClick={() => setInterested(false)}
          className={`h-12 rounded-xl text-sm font-bold ${interested === false ? "bg-slate-800 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"}`}>Not right now</button>
      </div>
      <NextBtn onClick={() => onNext({ interested })} disabled={interested === null} saving={saving} />
    </Card>
  );
}

function StepAdiRevenue({ v, onNext, saving }) {
  const [goal, setGoal] = useState(v.monthly_goal ?? "");
  return (
    <Card icon={PoundSterling} title="What do you want to earn each month?" subtitle="We'll shape your revenue dashboard around it. Optional.">
      <Input data-testid="onb-adi-goal" type="number" inputMode="decimal" value={goal}
        onChange={e => setGoal(e.target.value)} placeholder="e.g. 4000" />
      <NextBtn onClick={() => onNext({ monthly_goal: goal === "" ? null : Number(goal) })} saving={saving} label={goal ? "Continue" : "Skip for now"} />
    </Card>
  );
}

/* ═══════════════ PDI BRANCH ═══════════════ */
function StepPdiStage({ v, onNext, saving }) {
  const [stage, setStage] = useState(v.stage || "");
  const opts = [
    { key: "pre_part1", label: "Working towards Part 1 (theory)" },
    { key: "pre_part2", label: "Working towards Part 2 (driving)" },
    { key: "pre_part3", label: "Working towards Part 3 (instruction)" },
    { key: "on_trainee_licence", label: "On a trainee licence" },
  ];
  return (
    <Card icon={GraduationCap} title="Where are you in your training?">
      <div className="grid grid-cols-1 gap-2">
        {opts.map(o => (
          <button key={o.key} type="button" data-testid={`onb-pdi-stage-${o.key}`} onClick={() => setStage(o.key)}
            className={`h-12 rounded-xl text-sm font-semibold border text-left px-4 ${stage === o.key
              ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <NextBtn onClick={() => onNext({ stage })} disabled={!stage} saving={saving} />
    </Card>
  );
}

function StepPdiPart2({ v, onNext, saving }) {
  const [passed, setPassed] = useState(v.passed ?? null);
  const [date, setDate] = useState(v.passed_date || "");
  return (
    <Card icon={Check} title="Passed Part 2 yet?">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button data-testid="onb-part2-yes" type="button" onClick={() => setPassed(true)}
          className={`h-12 rounded-xl text-sm font-bold ${passed === true ? "bg-emerald-600 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"}`}>Yes</button>
        <button data-testid="onb-part2-no" type="button" onClick={() => setPassed(false)}
          className={`h-12 rounded-xl text-sm font-bold ${passed === false ? "bg-slate-800 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"}`}>Not yet</button>
      </div>
      {passed && (
        <>
          <Label>Pass date (optional)</Label>
          <Input data-testid="onb-part2-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1" />
        </>
      )}
      <NextBtn onClick={() => onNext({ passed, passed_date: date || null })} disabled={passed === null} saving={saving} />
    </Card>
  );
}

function StepPdiSupervisor({ v, onNext, saving }) {
  const [email, setEmail] = useState(v.supervisor_email || "");
  return (
    <Card icon={Users} title="Got a trainer in your corner?"
      subtitle="Pop their email in if you do — they'll see your progress and can leave notes.">
      <Label>Trainer email</Label>
      <Input data-testid="onb-pdi-supervisor" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="trainer@example.co.uk" className="mt-1" />
      <NextBtn onClick={() => onNext({ supervisor_email: email.trim().toLowerCase() || null })} saving={saving} label={email ? "Continue" : "Skip for now"} />
    </Card>
  );
}

function StepPdiBaseline({ v, onNext, saving }) {
  const [strengths, setStrengths] = useState(v.strengths || "");
  const [weaknesses, setWeaknesses] = useState(v.weaknesses || "");
  return (
    <Card icon={Target} title="Quick self-check"
      subtitle="Be honest — this shapes your Part 3 readiness dashboard.">
      <Label>What do you feel solid on?</Label>
      <Textarea data-testid="onb-pdi-strengths" rows={3} value={strengths} onChange={e => setStrengths(e.target.value)}
        placeholder="e.g. Client-centred learning, route planning..." className="mt-1 mb-4" />
      <Label>What's still tricky?</Label>
      <Textarea data-testid="onb-pdi-weaknesses" rows={3} value={weaknesses} onChange={e => setWeaknesses(e.target.value)}
        placeholder="e.g. Reflection prompts, risk management..." className="mt-1" />
      <NextBtn onClick={() => onNext({ strengths: strengths.trim(), weaknesses: weaknesses.trim() })} saving={saving} />
    </Card>
  );
}

function StepPdiTargetDate({ v, onNext, saving }) {
  const [date, setDate] = useState(v.target_date || "");
  return (
    <Card icon={Target} title="When do you want to sit Part 3?" subtitle="We'll pace your training backwards from there.">
      <Label>Aim to sit by</Label>
      <Input data-testid="onb-pdi-target" type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1" />
      <NextBtn onClick={() => onNext({ target_date: date || null })} saving={saving} label={date ? "Continue" : "Skip for now"} />
    </Card>
  );
}

/* ═══════════════ FINAL COMPLETION ═══════════════ */
const PLAN_TAGLINE = {
  basic:   "You're organised and ready to go.",
  premium: "Your admin systems are now working behind the scenes.",
  growth:  "Your business growth tools are now active.",
};
const PLAN_LABEL = { basic: "Basic", premium: "Premium", growth: "Growth" };

function StepComplete({ role, plan, onFinish, onViewDashboard, saving }) {
  const planKey = plan || "basic";
  const systems = [
    { label: "Calendar ready",             icon: CalendarIcon },
    { label: "Pricing ready",              icon: PoundSterling },
    { label: "Work Mode ready",            icon: Workflow },
    { label: `${PLAN_LABEL[planKey]} plan is now live`, icon: Sparkles },
  ];
  return (
    <div data-testid="activation-complete" className="space-y-4">
      <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-6 text-white shadow-xl">
        <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-4">
          <Rocket className="w-6 h-6" />
        </div>
        <h1 className="text-2xl font-extrabold leading-tight mb-1">Your business is now running smarter.</h1>
        <p className="text-sm text-white/85">{PLAN_TAGLINE[planKey]}</p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-5">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-3">Active systems</p>
        <ul className="space-y-2.5">
          {systems.map(({ label, icon: Icon }) => (
            <li key={label} data-testid={`system-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center shrink-0">
                <Check className="w-4 h-4" />
              </span>
              <span className="text-sm font-bold text-slate-900 dark:text-white flex-1">{label}</span>
              <Icon className="w-4 h-4 text-slate-400" />
            </li>
          ))}
        </ul>
      </div>

      <Button
        data-testid="activation-enter-work-mode"
        onClick={onFinish}
        disabled={saving}
        className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-xl"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{role === "pdi" ? "Open Trainee Hub" : "Enter Work Mode"}<ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
      <button
        data-testid="activation-view-dashboard"
        onClick={onViewDashboard}
        disabled={saving}
        className="w-full h-11 text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
      >
        View Dashboard
      </button>
    </div>
  );
}


/* ─── Specialisms step ─── */
function StepSpecialisms({ v, onNext, saving }) {
  const [selected, setSelected] = useState(v?.selected || []);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Persist to marketplace profile so it's live immediately on the public profile.
      await axios.put(
        `${API}/api/marketplace/my-profile`,
        { specialties: selected },
        { withCredentials: true },
      );
    } catch {
      // Soft-fail — onboarding step still advances; user can edit later.
    }
    setSubmitting(false);
    onNext({ selected });
  };

  return (
    <Card
      icon={Award}
      testId="onb-specialisms"
      title="What do you specialise in?"
      subtitle="Pick the areas you're strongest in. Learners use this to find the right fit."
    >
      <SpecialtyPicker value={selected} onChange={setSelected} testidPrefix="onb-spec" />

      <Button
        data-testid="onb-specialisms-next"
        onClick={submit}
        disabled={saving || submitting}
        className="w-full h-12 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl"
      >
        {(saving || submitting) ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>{selected.length === 0 ? "Skip for now" : `Continue with ${selected.length} ${selected.length === 1 ? "specialism" : "specialisms"}`}<ArrowRight className="w-4 h-4 ml-2" /></>
        )}
      </Button>
      <p className="mt-2 text-[11px] text-slate-400 text-center">
        You can change these any time from your Passport or Marketplace settings.
      </p>
    </Card>
  );
}
